import * as React from 'react';
import SignaturePad from 'react-signature-canvas';
import styled from 'styled-components';
import { publicIpv4, publicIpv6 } from 'public-ip';
import { format } from 'date-fns';
import * as Sentry from '@sentry/react';
import PrimaryButton from '../../appComponents/common/buttons/PrimaryButton';
import SecondaryButton from '../../appComponents/common/buttons/SecondaryButton';
import { getCandidateIpAddress, getFile, setCandidateSignature } from '../../core/api';
import ConsentSignature from '../../models/be/ConsentSignature';
import { apiRootUrl } from '../../config.local';
import AppApi from '../../core/AppApi';
import { getQueryStringForDocumentUpload } from '../../appComponents/common/dataCapture/DocumentField';
import { hasValue } from '../../lib/utils';
import { hasCandidateRole } from '../../core/session';
import Loading from '../Loading';

const Canvas = (props: Props) => {
	const [dataURL, setDataURL] = React.useState<string | null>(null);
	const [dirty, setDirty] = React.useState<boolean>(false);
	const [workflowImage, setWorkflowImage] = React.useState<string | null>(null);
	const [showCanvas, setShowCanvas] = React.useState<boolean>(true);
	const [ipAddress, setIpAddress] = React.useState<string>('');
	const [saving, setSaving] = React.useState<boolean>(false);

	React.useEffect(() => {
		const fetchData = async () => {
			try {
				const isCandidate = hasCandidateRole();
				let candidateIpaddress: string | null = null;

				if (isCandidate) {
					candidateIpaddress = await getCandidateIpAddress();
					setIpAddress(candidateIpaddress);
				}

				if (!hasValue(candidateIpaddress)) {
					// Try fetching IPv4 address first
					const ipv4Address = await publicIpv4();
					if (ipv4Address) {
						setIpAddress(ipv4Address);
					}
				}
			} catch (ipv4AddressError) {
				try {
					// If fetching IPv4 failed, try fetching IPv6 address
					const ipv6Address = await publicIpv6();
					if (ipv6Address) {
						setIpAddress(ipv6Address);
					}
				} catch (ipv6AddressError) {
					// If both IPv4 and IPv6 fetching fail, capture the error using Sentry
					Sentry.captureMessage(ipv6AddressError, { level: 'warning' });
				}
			}
		};
		props.setSignatureUploaded && props.setSignatureUploaded(false);
		fetchData();
	}, []);

	React.useEffect(() => {
		(async () => {
			if (props.consentSignature.imageUrl) {
				try {
					setShowCanvas(false);
					props.setSignatureUploaded && props.setSignatureUploaded(true);
					if (props.consentSignature.imageDataUrl) {
						setWorkflowImage(props.consentSignature.imageDataUrl);
						setDataURL(props.consentSignature.imageDataUrl);
					} else {
						const fileString = await getSignature(props.consentSignature.imageUrl);
						if (fileString) {
							setWorkflowImage(`data:image/png;base64,${fileString}`);
							setDataURL(fileString);
						}
					}
				} catch (error) {
					Sentry.captureException(error);
					setShowCanvas(true);
					props.setSignatureUploaded && props.setSignatureUploaded(false);
				}
			}
		})();
	}, []);

	const padRef = React.useRef<SignaturePad>(null);

	const clear = () => {
		padRef.current?.clear();
		setDataURL(null);
		setShowCanvas(true);
		props.setSignatureUploaded && props.setSignatureUploaded(false);
	};

	const save = async () => {
		if (dirty) {
			try {
				setSaving(true);
				const url = padRef.current?.getTrimmedCanvas().toDataURL('image/png');
				if (url) {
					setDataURL(url);
					setShowCanvas(false);
					setWorkflowImage(url);
					setDirty(false);
					await saveSignature(url);
				}
			} catch (error) {
				Sentry.captureException(error);
				setShowCanvas(true);
				setDirty(true);
				setDataURL(null);
				setWorkflowImage(null);
			} finally {
				setSaving(false);
			}
		}
	};

	const saveSignature = async (base64ImageString: string) => {
		const base64Response = await fetch(base64ImageString);
		const blob = await base64Response.blob();
		const formData = new FormData();
		formData.append('data', blob, `${props.workflowId}-consent.png`);
		const token = await AppApi.getOrGenerateAccessToken();
		const queryString = getQueryStringForDocumentUpload(props.candidateId, props.customerId, props.workflowId);

		const saveRequestPromise = new Promise<void>((resolve, reject) => {
			const xhr = new XMLHttpRequest();

			xhr.addEventListener('loadend', async (event) => {
				const xhrCompletedReadyState = 4;
				try {
					if (xhr.readyState === xhrCompletedReadyState && xhr.status === 200) {
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						const responseblob = { ...JSON.parse((event.target as any).response) };
						await saveSignatureToWorkflow(responseblob.fileId, base64ImageString);
						resolve();
					} else {
						// eslint-disable-next-line prefer-promise-reject-errors
						reject();
					}
				} catch (error) {
					reject(error);
				}
			});

			xhr.open('post', `${apiRootUrl}/api/Files/RequestId${queryString}`);
			xhr.setRequestHeader('Authorization', `Bearer ${token}`);
			xhr.send(formData);
		});
		return saveRequestPromise;
	};

	const getSignature = async (fileId: string) => {
		const v = await getFile(fileId, props.token);
		setWorkflowImage(`data:image/png;base64,${v}`);
		return v;
	};

	const saveSignatureToWorkflow = async (imageUrl: string, imageDataUrl: string) => {
		const consentSignature: ConsentSignature = {
			consentTime: new Date(),
			imageUrl,
			ipAddress
		};
		await setCandidateSignature(consentSignature, props.workflowId);
		props.onUpload && props.onUpload({ ...consentSignature, imageDataUrl });
	};

	const content = () => (
		<>
			<Row>
				<Cell>
					<Label>IP Address</Label>
				</Cell>
				<Cell>{ipAddress}</Cell>
			</Row>
			<Row>
				<Cell>
					<Label>Verified Email</Label>
				</Cell>
				<Cell>{props.email}</Cell>
			</Row>
			<Row>
				<Cell>
					<Label>Time Stamp</Label>
				</Cell>
				<Cell>
					{props.consentSignature.consentTime
						? format(props.consentSignature.consentTime, 'hh:mm a dd/MM/yyyy')
						: format(new Date(), 'hh:mm a dd/MM/yyyy')}
				</Cell>
			</Row>
		</>
	);

	return (
		<CanvasPad>
			{saving && <Loading usePortal={false} />}
			<Row>
				<Cell>
					<Label>Signature</Label>
				</Cell>
				<Cell>
					<ImageWrapper>
						{dataURL || workflowImage ? (
							<Signature src={workflowImage ?? dataURL ?? ''} alt="user generated signature" />
						) : null}
					</ImageWrapper>
				</Cell>
			</Row>
			<Row>
				<Cell>
					<ImageWrapper>
						{showCanvas && (
							<SignaturePad onEnd={() => setDirty(true)} ref={padRef} canvasProps={{ className: 'signCanvas' }} />
						)}
					</ImageWrapper>
				</Cell>
			</Row>
			{showCanvas && props.errorMessage && (
				<Row>
					<Cell>
						<ErrorMessage>{props.errorMessage}</ErrorMessage>
					</Cell>
				</Row>
			)}
			{!showCanvas && content()}
			{props.showButtons && (
				<ButtonWrapper>
					{showCanvas && <PrimaryButton text="Save" type="submit" onClick={save} disabled={saving} />}
					<SecondaryButton text={showCanvas ? 'Cancel' : 'Reset'} onClick={clear} />
				</ButtonWrapper>
			)}
		</CanvasPad>
	);
};

export default Canvas;

interface Props {
	workflowId: string;
	email: string;
	consentSignature: ConsentSignature;
	candidateId: string;
	customerId: number;
	onUpload: (consent: ConsentSignature) => void;
	errorMessage: string;
	showButtons: boolean;
	token: string;
	setSignatureUploaded: (value: boolean) => void;
}

const CanvasPad = styled.div`
	&& {
		display: block;

		@media (max-width: 375px) {
			max-width: 200px;
		}
	}
`;

const ImageWrapper = styled.section`
	.signCanvas {
		height: 135px;
		display: block;
		border: 1px solid rgba(38, 38, 38, 0.2);
		border-radius: 8px;
		background-color: #fff;
	}
`;

const Signature = styled.img`
	height: 70px;
	width: 150px;
	border-radius: 8px;
`;

const Cell = styled.div`
	padding: 0 10px;
`;

const Label = styled.div`
	width: 100px;
`;

const Row = styled.div`
	margin-top: 6px;
	display: flex;
`;

const ButtonWrapper = styled.div`
	text-align: right;
	margin: 10px 0 0 5px;
	display: flex;
`;

const ErrorMessage = styled.div`
	color: #ff5151;
	font-size: 12px;
`;
